import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import {
    COMPANY_SIZE_OPTIONS,
    INTERLOCUTOR_TYPE_OPTIONS,
    NATIVE_LANGUAGE_OPTIONS,
    TEAM_SIZE_OPTIONS,
    NativeLanguageOption,
    PersonalizationAnswers,
    PersonalizationOption,
    emptyPersonalizationAnswers,
} from '../../../model/ai-personalization.model';

/**
 * Legacy — ancien écran Tailwind de personnalisation métier.
 * Remplacé par `AI-exam-onboarding-professional-universe` en mode
 * `personalizationOnly` pour LILATE step 3b ; conservé pour référence.
 *
 * Questions fermées uniquement :
 * - langue maternelle (préremplie, à confirmer ou corriger),
 * - taille d'entreprise (obligatoire),
 * - interlocuteurs habituels (multi-choix, skippable),
 * - position de management (oui/non, skippable),
 * - taille d'équipe (si manager, skippable).
 *
 * Le composant émet l'état des réponses à chaque changement ; le parent
 * gère le bouton de validation du step (désactivé tant que la taille
 * d'entreprise n'est pas renseignée).
 *
 * Usage :
 * <lib-ai-exam-onboarding-personalization
 *   [answers]="personalizationAnswers()"
 *   (answersChange)="onPersonalizationChange($event)">
 * </lib-ai-exam-onboarding-personalization>
 */
@Component({
    selector: 'lib-ai-exam-onboarding-personalization',
    standalone: true,
    imports: [CommonModule, MatIconModule, TranslateModule],
    templateUrl: './AI-exam-onboarding-personalization.component.html',
    styleUrls: ['./AI-exam-onboarding-personalization.component.scss'],
})
export class AIExamOnboardingPersonalizationComponent {
    @Input() answers: PersonalizationAnswers = emptyPersonalizationAnswers();
    /** Masque la question langue maternelle (collectée à l'écran bienvenue). */
    @Input() hideNativeLanguage = false;
    @Output() answersChange = new EventEmitter<PersonalizationAnswers>();

    companySizeOptions: PersonalizationOption[] = COMPANY_SIZE_OPTIONS;
    interlocutorOptions: PersonalizationOption[] = INTERLOCUTOR_TYPE_OPTIONS;
    teamSizeOptions: PersonalizationOption[] = TEAM_SIZE_OPTIONS;
    nativeLanguageOptions: NativeLanguageOption[] = NATIVE_LANGUAGE_OPTIONS;

    selectNativeLanguage(value: string): void {
        this.emitAnswers({ ...this.answers, native_language: value || null });
    }

    selectCompanySize(value: string): void {
        this.emitAnswers({ ...this.answers, company_size: value });
    }

    toggleInterlocutor(value: string): void {
        const current = this.answers.interlocutor_types;
        const interlocutor_types = current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
        this.emitAnswers({ ...this.answers, interlocutor_types });
    }

    selectIsManager(value: boolean): void {
        // Re-cliquer sur la réponse active la désélectionne (retour à "skippé").
        const is_manager = this.answers.is_manager === value ? null : value;
        this.emitAnswers({
            ...this.answers,
            is_manager,
            // La taille d'équipe n'a de sens que pour un manager déclaré.
            team_size: is_manager === true ? this.answers.team_size : null,
        });
    }

    selectTeamSize(value: string): void {
        const team_size = this.answers.team_size === value ? null : value;
        this.emitAnswers({ ...this.answers, team_size });
    }

    isInterlocutorSelected(value: string): boolean {
        return this.answers.interlocutor_types.includes(value);
    }

    private emitAnswers(answers: PersonalizationAnswers): void {
        this.answers = answers;
        this.answersChange.emit(answers);
    }
}
